import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './TextBox.module.css';

// 日期工具函数
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const TextBox = ({
  label = "标签",
  labelWidth, // 新增：label宽度属性
  onChange,
  value,
  searchList = [],
  leftIcon = "#icon-edit",
  rightIcon = "#icon-a-duicuocuo",
  Type = "SearchBox",
  min,
  max,
  step = 1,
  placeholder,
  dateFormat = 'YYYY-MM-DD',
  editable = true,
  multiple = false,
  connector = '、',
  trueLabel = "是",
  falseLabel = "否",
  onLabelDoubleClick,
  enableInputSearch = true,
  showCondition = true,
  multiline = false,
  tooltip = '',
  tooltipDelay = 300,
  tooltipPosition = 'top',
}) => {
  const [inputValue, setInputValue] = useState(() => {
    if (Type === "Switch") {
      if (value === true) return trueLabel;
      if (value === false) return falseLabel;
      return '';
    }
    return value || '';
  });

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const defaultPlaceholder = placeholder || (
    Type === "DatePicker" ? "请选择日期" :
      Type === "NumberInput" ? "请输入数字" :
        Type === "ComboBox" ? "请选择或输入..." :
          Type === "Switch" ? "请选择..." :
            "请输入内容..."
  );
  const inputRef = useRef(null);

  // Tooltip 相关状态
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPositionState, setTooltipPositionState] = useState({ top: 0, left: 0 });
  const tooltipTimeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const rafIdRef = useRef(null);

  // 新增：NumberInput 数字键盘面板显示状态
  const [isNumberPadOpen, setIsNumberPadOpen] = useState(false);

  // ComboBox 多选相关状态
  const [selectedItems, setSelectedItems] = useState(() => {
    if (value && multiple) {
      if (typeof value === 'string') {
        return value.split(connector).map(s => s.trim()).filter(s => s);
      }
      return Array.isArray(value) ? value : [];
    }
    return [];
  });

  // Switch 模式内部状态
  const [switchValue, setSwitchValue] = useState(() => {
    if (Type === "Switch") {
      if (value === true) return true;
      if (value === false) return false;
      return null;
    }
    return null;
  });

  // Tooltip 位置计算 - 关键修复：在计算时就考虑 transform 偏移
  const calculateTooltipPosition = () => {
    if (!inputWrapperRef.current) return;

    const rect = inputWrapperRef.current.getBoundingClientRect();
    let top = 0;
    let left = 0;

    switch (tooltipPosition) {
      case 'top':
        top = rect.top - 8;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - 8;
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + 8;
        break;
      default:
        top = rect.top - 8;
        left = rect.left + rect.width / 2;
    }

    setTooltipPositionState({ top, left });
  };

  // 立即计算位置并显示（不使用 RAF）
  const showTooltipImmediately = () => {
    calculateTooltipPosition();
    setShowTooltip(true);
  };

  // Tooltip 显示处理
  const handleMouseEnter = () => {
    if (!tooltip) return;

    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }

    tooltipTimeoutRef.current = setTimeout(() => {
      showTooltipImmediately();
    }, tooltipDelay);
  };

  const handleMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  // 清理
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // 同步外部 value
  useEffect(() => {
    if (value !== undefined && value !== null) {
      if (Type === "ComboBox" && multiple) {
        if (typeof value === 'string') {
          const items = value.split(connector).map(s => s.trim()).filter(s => s);
          setSelectedItems(items);
          setInputValue(value);
        } else if (Array.isArray(value)) {
          setSelectedItems(value);
          setInputValue(value.join(connector));
        }
      } else if (Type === "Switch") {
        if (value === true) {
          setInputValue(trueLabel);
          setSwitchValue(true);
        } else if (value === false) {
          setInputValue(falseLabel);
          setSwitchValue(false);
        } else {
          setInputValue('');
          setSwitchValue(null);
        }
      } else if (Type === "DatePicker") {
        if (value) {
          try {
            const displayValue = formatDate(value, dateFormat);
            setInputValue(displayValue);
            const parsedDate = parseDate(value);
            if (parsedDate && !isNaN(parsedDate.getTime())) {
              setTempDate(parsedDate);
            } else {
              const today = new Date();
              setTempDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
            }
          } catch (e) {
            console.warn('Date parsing error:', e);
            setInputValue('');
            const today = new Date();
            setTempDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
          }
        } else {
          setInputValue('');
          const today = new Date();
          setTempDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
        }
      } else if (value !== inputValue) {
        setInputValue(value);
      }
    }
  }, [value, Type, multiple, connector, trueLabel, falseLabel, dateFormat]);

  // 日期选择器相关状态
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [viewMode, setViewMode] = useState('day');
  const [tempDate, setTempDate] = useState(() => {
    if (value && Type === "DatePicker") return parseDate(value);
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  });
  const [yearDecadeStart, setYearDecadeStart] = useState(() => {
    const year = (value && Type === "DatePicker" ? parseDate(value) : new Date()).getFullYear();
    return Math.floor(year / 10) * 10;
  });

  // 同步年份 decade
  useEffect(() => {
    if (Type === "DatePicker") {
      const year = tempDate.getFullYear();
      setYearDecadeStart(Math.floor(year / 10) * 10);
    }
  }, [tempDate, Type]);

  // 解析日期
  function parseDate(dateStr) {
    if (!dateStr) return new Date();

    if (dateStr instanceof Date) {
      return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
    }

    if (typeof dateStr === 'number') {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) return date;
    }

    const str = String(dateStr);

    let match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));

    match = str.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (match) return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));

    match = str.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日$/);
    if (match) return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));

    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  // 格式化日期
  function formatDate(dateInput, formatStr = dateFormat) {
    if (!dateInput) return '';

    let d;
    if (dateInput instanceof Date) {
      d = new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    } else {
      const parsed = parseDate(dateInput);
      if (isNaN(parsed.getTime())) return '';
      d = parsed;
    }

    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();

    let result = formatStr;
    result = result.replace('YYYY', year.toString());

    if (formatStr.includes('MM')) {
      result = result.replace('MM', String(month).padStart(2, '0'));
    } else {
      result = result.replace('M', month.toString());
    }

    if (formatStr.includes('DD')) {
      result = result.replace('DD', String(day).padStart(2, '0'));
    } else {
      result = result.replace('D', day.toString());
    }

    return result;
  }

  // 日历数据
  const calendarDays = useMemo(() => {
    if (Type !== "DatePicker") return [];
    const year = tempDate.getFullYear();
    const month = tempDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);

    let firstDay = getFirstDayOfMonth(year, month);
    firstDay = firstDay === 0 ? 6 : firstDay - 1;

    const prevMonthDays = getDaysInMonth(year, month - 1);
    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  }, [tempDate, Type]);

  // 十年视图年份列表
  const decadeYears = useMemo(() => {
    const years = [];
    years.push(yearDecadeStart - 1);
    for (let i = 0; i < 10; i++) {
      years.push(yearDecadeStart + i);
    }
    years.push(yearDecadeStart + 10);
    return years;
  }, [yearDecadeStart]);

  const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  // 日期选择处理
  const handleSelectDate = (date) => {
    const isoFormat = formatDate(date, 'YYYY-MM-DD');
    const displayFormat = formatDate(date, dateFormat);

    setInputValue(displayFormat);
    onChange?.(isoFormat);
    setIsDatePickerOpen(false);
    setViewMode('day');
  };

  const handleSelectMonth = (month) => {
    setTempDate(prev => new Date(prev.getFullYear(), month, 1));
    setViewMode('day');
  };

  const handleSelectYear = (year) => {
    setTempDate(prev => new Date(year, prev.getMonth(), 1));
    setViewMode('month');
  };

  const handleToday = () => {
    const today = new Date();
    const isoFormat = formatDate(today, 'YYYY-MM-DD');
    const displayFormat = formatDate(today, dateFormat);

    setInputValue(displayFormat);
    onChange?.(isoFormat);
    setTempDate(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
    setIsDatePickerOpen(false);
    setViewMode('day');
  };

  const isSelected = (date) => {
    if (!inputValue) return false;
    const parsed = parseDate(inputValue);
    return date.getFullYear() === parsed.getFullYear() &&
      date.getMonth() === parsed.getMonth() &&
      date.getDate() === parsed.getDate();
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();
  };

  // 日期导航
  const toPrevMonth = () => setTempDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const toNextMonth = () => setTempDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  const toPrevYear = () => setTempDate(prev => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  const toNextYear = () => setTempDate(prev => new Date(prev.getFullYear() + 1, prev.getMonth(), 1));
  const toPrevDecade = () => setYearDecadeStart(prev => prev - 10);
  const toNextDecade = () => setYearDecadeStart(prev => prev + 10);

  // 处理主输入框的变化
  const handleInputChange = (e) => {
    let val = e.target.value;

    if (Type === "NumberInput") {
      val = val.replace(/[^0-9.-]/g, '');
      if ((val.match(/\./g) || []).length > 1) return;
      if ((val.match(/-/g) || []).length > 1) return;
      if (val.indexOf('-') > 0) return;
      setInputValue(val);
      onChange && onChange(val === '' ? '' : Number(val));
    } else if (Type === "ComboBox") {
      if (!editable) return;
      setInputValue(val);
      if (enableInputSearch) {
        setSearchQuery(val);
      }
      if (!multiple) {
        setSelectedItems([]);
        onChange?.(val);
      } else {
        onChange?.(val);
      }
    } else if (Type === "SearchBox") {
      setInputValue(val);
      onChange && onChange(val);
      setSearchQuery(val || '');
      if (!isDropdownVisible) {
        setIsDropdownVisible(true);
      }
    } else if (Type === "Switch") {
      return;
    } else {
      setInputValue(val);
      onChange && onChange(val);
    }
  };

  // 清空输入框
  const handleClear = () => {
    if (Type === "Switch") {
      setInputValue('');
      setSwitchValue(null);
      setSearchQuery('');
      setIsDropdownVisible(true);
      onChange?.(null);
    } else {
      setInputValue('');
      setSearchQuery('');

      if (Type === "ComboBox" && multiple) {
        setSelectedItems([]);
      }

      if (Type === "ComboBox" && !multiple) {
        setSelectedItems([]);
      }

      onChange && onChange(multiple ? [] : '');

      if (Type === "SearchBox" || Type === "ComboBox") {
        setIsDropdownVisible(true);
      }
    }

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // NumberInput 增减
  const handleIncrement = () => {
    const currentVal = inputValue === '' ? 0 : Number(inputValue);
    const newVal = currentVal + step;
    if (max !== undefined && newVal > max) return;
    setInputValue(newVal);
    onChange && onChange(newVal);
    inputRef.current?.focus();
  };

  const handleDecrement = () => {
    const currentVal = inputValue === '' ? 0 : Number(inputValue);
    const newVal = currentVal - step;
    if (min !== undefined && newVal < min) return;
    setInputValue(newVal);
    onChange && onChange(newVal);
    inputRef.current?.focus();
  };

  // 新增：NumberInput 数字键盘按键处理
  const handleNumberPadPress = (key) => {
    let val = String(inputValue ?? '');

    if (key === 'x') {
      // 删除最后一位
      val = val.slice(0, -1);
    } else if (key === '.') {
      // 已有小数点则忽略
      if (val.includes('.')) return;
      // 空值或只有负号时，补 0
      if (val === '' || val === '-') val = `${val}0`;
      val = `${val}.`;
    } else {
      // 数字键
      if (val === '0') {
        val = key; // 避免 0 开头，如 07 -> 7
      } else {
        val = `${val}${key}`;
      }
    }

    // 校验 min / max（仅当能解析为数字时）
    const num = val === '' || val === '-' || val === '.' ? null : Number(val);
    if (num !== null && !isNaN(num)) {
      if (min !== undefined && num < min) return;
      if (max !== undefined && num > max) return;
    }

    setInputValue(val);
    onChange && onChange(val === '' ? '' : (isNaN(Number(val)) ? val : Number(val)));
  };

  // 新增：点击 leftIcon 切换数字键盘
  const handleLeftIconClick = () => {
    if (Type !== "NumberInput") return;
    setIsNumberPadOpen(prev => !prev);
    setIsDropdownVisible(false);
    setIsDatePickerOpen(false);
  };

  // 输入框获得焦点时显示下拉
  const handleInputFocus = () => {
    if (Type === "SearchBox" || Type === "ComboBox" || Type === "Switch") {
      setIsDropdownVisible(true);
      if (enableInputSearch && (Type === "SearchBox" || Type === "ComboBox") && inputValue) {
        setSearchQuery(inputValue);
      }
    }
  };

  // 点击输入框区域（DatePicker 模式）
  const handleDatePickerClick = () => {
    if (Type === "DatePicker") {
      setIsDatePickerOpen(!isDatePickerOpen);
      setViewMode('day');
      setIsDropdownVisible(false);
    }
  };

  // Switch 模式
  const handleSwitchSelect = (newValue) => {
    const displayText = newValue === true ? trueLabel : falseLabel;
    setInputValue(displayText);
    setSwitchValue(newValue);
    onChange?.(newValue);
    setIsDropdownVisible(false);
  };

  // ComboBox 多选
  const handleCheckboxChange = (item, isChecked) => {
    let newSelectedItems;
    if (isChecked) {
      newSelectedItems = [...selectedItems, item];
    } else {
      newSelectedItems = selectedItems.filter(selected => selected !== item);
    }

    setSelectedItems(newSelectedItems);
    const displayValue = newSelectedItems.join(connector);
    setInputValue(displayValue);
    onChange?.(multiple ? newSelectedItems : displayValue);
  };

  // ComboBox 单选
  const handleSingleSelect = (item) => {
    setInputValue(item);
    setSelectedItems([item]);
    onChange?.(item);
    setIsDropdownVisible(false);
    setSearchQuery('');
  };

  // SearchBox 选择
  const handleSelectItem = (item) => {
    setInputValue(item);
    onChange && onChange(item);
    setIsDropdownVisible(false);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  // 搜索输入
  const handleSearchInputChange = (e) => {
    e.stopPropagation();
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
  };

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
        setSearchQuery('');
        setIsDatePickerOpen(false);
        setViewMode('day');
        setIsNumberPadOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 搜索过滤
  const getFilteredData = () => {
    if (Type === "SearchBox" && !searchQuery.trim()) {
      return searchList;
    }

    return searchList.filter(item =>
      item.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredData = getFilteredData();

  const handleKeyDown = (e) => {
    if (Type === "NumberInput") {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleIncrement();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        handleDecrement();
      }
    }
  };

  // 渲染各种面板
  const renderSwitchPanel = () => {
    if (Type !== "Switch" || !isDropdownVisible) return null;

    const options = [
      { label: trueLabel, value: true },
      { label: falseLabel, value: false }
    ];

    return (
      <div className={styles.dropdownPanel}>
        <ul className={styles.resultList}>
          {options.map((option) => (
            <li
              key={option.label}
              className={`${styles.resultItem} ${switchValue === option.value ? styles.selectedItem : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSwitchSelect(option.value);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderSearchBoxPanel = () => {
    if (Type !== "SearchBox" || !isDropdownVisible || searchList.length === 0) return null;

    return (
      <div className={styles.dropdownPanel}>
        {!enableInputSearch && (
          <input
            type="text"
            className={styles.dropdownSearchInput}
            placeholder="搜索选项..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        <ul className={styles.resultList}>
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <li
                key={index}
                className={styles.resultItem}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectItem(item);
                }}
              >
                {item}
              </li>
            ))
          ) : (
            <li className={styles.resultItem} style={{ color: '#999' }}>无匹配内容</li>
          )}
        </ul>
      </div>
    );
  };

  const renderComboBoxPanel = () => {
    if (Type !== "ComboBox" || !isDropdownVisible) return null;

    return (
      <div className={styles.dropdownPanel}>
        {editable && !enableInputSearch && (
          <input
            type="text"
            className={styles.dropdownSearchInput}
            placeholder="搜索选项..."
            value={searchQuery}
            onChange={handleSearchInputChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}
        <ul className={styles.resultList}>
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => {
              const isChecked = selectedItems.includes(item);

              if (multiple) {
                return (
                  <li
                    key={index}
                    className={`${styles.resultItem} ${styles.checkboxItem}`}
                    onClick={() => handleCheckboxChange(item, !isChecked)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <span className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => { }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </span>
                    <span className={styles.itemText}>{item}</span>
                  </li>
                );
              } else {
                return (
                  <li
                    key={index}
                    className={`${styles.resultItem} ${selectedItems[0] === item ? styles.selectedItem : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSingleSelect(item);
                    }}
                  >
                    {item}
                  </li>
                );
              }
            })
          ) : (
            <li className={styles.resultItem} style={{ color: '#999' }}>无匹配内容</li>
          )}
        </ul>
      </div>
    );
  };

  // 新增：NumberInput 数字键盘面板
  const renderNumberPadPanel = () => {
    if (Type !== "NumberInput" || !isNumberPadOpen) return null;

    const keys = [
      '7', '8', '9',
      '4', '5', '6',
      '1', '2', '3',
      '0', '.', 'x',
    ];

    return (
      <div className={styles.numberPadPanel}>
        <div className={styles.numberPadGrid}>
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              className={`${styles.numberPadKey} ${key === 'x' ? styles.numberPadKeyDelete : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                handleNumberPadPress(key);
              }}
            >
              {key}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderDatePickerPanel = () => {
    if (Type !== "DatePicker" || !isDatePickerOpen) return null;

    const renderHeader = () => {
      if (viewMode === 'day') {
        return (
          <div className={styles.dateHeaderBar}>
            <button className={styles.dateNavBtn} onClick={toPrevYear} title="上一年">«</button>
            <button className={styles.dateNavBtn} onClick={toPrevMonth} title="上一月">‹</button>
            <div className={styles.dateTitleGroup}>
              <span className={styles.dateClickableTitle} onClick={() => setViewMode('year')}>
                {tempDate.getFullYear()}年
              </span>
              <span className={styles.dateClickableTitle} onClick={() => setViewMode('month')}>
                {tempDate.getMonth() + 1}月
              </span>
            </div>
            <button className={styles.dateNavBtn} onClick={toNextMonth} title="下一月">›</button>
            <button className={styles.dateNavBtn} onClick={toNextYear} title="下一年">»</button>
          </div>
        );
      }

      if (viewMode === 'month') {
        return (
          <div className={styles.dateHeaderBar}>
            <button className={styles.dateNavBtn} onClick={toPrevYear} title="上一年">«</button>
            <span className={styles.dateClickableTitle} onClick={() => setViewMode('year')}>
              {tempDate.getFullYear()}年
            </span>
            <button className={styles.dateNavBtn} onClick={toNextYear} title="下一年">»</button>
          </div>
        );
      }

      if (viewMode === 'year') {
        return (
          <div className={styles.dateHeaderBar}>
            <button className={styles.dateNavBtn} onClick={toPrevDecade} title="前十年">«</button>
            <span className={styles.dateNormalTitle}>
              {yearDecadeStart}年 - {yearDecadeStart + 9}年
            </span>
            <button className={styles.dateNavBtn} onClick={toNextDecade} title="后十年">»</button>
          </div>
        );
      }
    };

    const renderContent = () => {
      if (viewMode === 'day') {
        return (
          <>
            <div className={styles.dateWeekdays}>
              {['一', '二', '三', '四', '五', '六', '日'].map(day => (
                <div key={day} className={styles.dateWeekday}>{day}</div>
              ))}
            </div>
            <div className={styles.dateDaysGrid}>
              {calendarDays.map((item, index) => {
                const date = item.date;
                return (
                  <div
                    key={index}
                    className={`${styles.dateDayCell} ${!item.isCurrentMonth ? styles.dateOtherMonth : ''} ${isSelected(date) ? styles.dateSelected : ''} ${isToday(date) ? styles.dateToday : ''}`}
                    onClick={() => handleSelectDate(date)}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
            <div className={styles.dateFooter}>
              <button className={styles.dateTodayBtn} onClick={handleToday}>今天</button>
            </div>
          </>
        );
      }

      if (viewMode === 'month') {
        return (
          <div className={styles.dateMonthGrid}>
            {months.map((month, index) => (
              <div
                key={month}
                className={`${styles.dateMonthCell} ${tempDate.getMonth() === index ? styles.dateActive : ''}`}
                onClick={() => handleSelectMonth(index)}
              >
                {month}
              </div>
            ))}
          </div>
        );
      }

      if (viewMode === 'year') {
        const currentYear = tempDate.getFullYear();
        return (
          <div className={styles.dateYearGrid}>
            {decadeYears.map((year) => {
              const isInDecade = year >= yearDecadeStart && year <= yearDecadeStart + 9;
              return (
                <div
                  key={year}
                  className={`${styles.dateYearCell} ${!isInDecade ? styles.dateOutOfDecade : ''} ${currentYear === year ? styles.dateActive : ''}`}
                  onClick={() => isInDecade && handleSelectYear(year)}
                >
                  {year}
                </div>
              );
            })}
          </div>
        );
      }
    };

    return (
      <div className={styles.datePickerPanel}>
        {renderHeader()}
        <div className={styles.datePanelBody}>
          {renderContent()}
        </div>
      </div>
    );
  };

  const renderInput = () => {
    if (multiline && (Type === "SearchBox" || Type === "ComboBox")) {
      return (
        <textarea
          ref={inputRef}
          className={styles.textarea}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={defaultPlaceholder}
          readOnly={Type === "ComboBox" && !editable}
          style={{
            resize: 'vertical',
          }}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        className={`${styles.input} ${Type === "NumberInput" ? styles.numberInput : ''} 
          ${Type === "DatePicker" ? styles.dateInput : ''}`}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={defaultPlaceholder}
        inputMode={Type === "NumberInput" ? "numeric" : "text"}
        readOnly={Type === "DatePicker" || Type === "Switch" || (Type === "ComboBox" && !editable)}
        onClick={(e) => {
          if (Type === "DatePicker") {
            e.stopPropagation();
            handleDatePickerClick();
          }
        }}
      />
    );
  };

  if (!showCondition) return null;

  // 关键修复：tooltip 样式 - 根据不同方向应用正确的 transform
  const getTooltipStyle = () => {
    const baseStyle = {
      position: 'fixed',
      zIndex: 1000,
    };

    // 根据方向设置不同的位置和 transform
    switch (tooltipPosition) {
      case 'top':
        return {
          ...baseStyle,
          bottom: `calc(100vh - ${tooltipPositionState.top}px)`,
          left: `${tooltipPositionState.left}px`,
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          ...baseStyle,
          top: `${tooltipPositionState.top}px`,
          left: `${tooltipPositionState.left}px`,
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          ...baseStyle,
          top: `${tooltipPositionState.top}px`,
          right: `calc(100vw - ${tooltipPositionState.left}px)`,
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          ...baseStyle,
          top: `${tooltipPositionState.top}px`,
          left: `${tooltipPositionState.left}px`,
          transform: 'translateY(-50%)',
        };
      default:
        return {
          ...baseStyle,
          top: `${tooltipPositionState.top}px`,
          left: `${tooltipPositionState.left}px`,
          transform: 'translateX(-50%)',
        };
    }
  };

  const getTooltipClassName = () => {
    const baseClass = styles.tooltip;
    switch (tooltipPosition) {
      case 'top': return `${baseClass} ${styles.tooltipTop}`;
      case 'bottom': return `${baseClass} ${styles.tooltipBottom}`;
      case 'left': return `${baseClass} ${styles.tooltipLeft}`;
      case 'right': return `${baseClass} ${styles.tooltipRight}`;
      default: return `${baseClass} ${styles.tooltipTop}`;
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <label
        className={styles.label}
        style={labelWidth ? { width: labelWidth, minWidth: labelWidth } : {}}
        onDoubleClick={(e) => {
          if (onLabelDoubleClick) {
            onLabelDoubleClick(e);
          }
        }}
      >
        {label}
      </label>

      <div
        className={styles.inputWrapper}
        ref={inputWrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* 左边图标 */}
        <svg
          className={`${styles.icon} ${Type === "NumberInput" ? styles.leftIconClickable : ''}`}
          aria-hidden="true"
          onClick={Type === "NumberInput" ? handleLeftIconClick : undefined}
        >
          <use xlinkHref={leftIcon}></use>
        </svg>

        {renderInput()}

        {/* 清空图标 */}
        {((Type === "SearchBox" || Type === "ComboBox") && inputValue && !multiline) ||
          (Type === "Switch" && inputValue) ? (
          <svg
            className={`${styles.icon} ${styles.rightIcon}`}
            aria-hidden="true"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
          >
            <use xlinkHref={rightIcon}></use>
          </svg>
        ) : null}

        {/* 多行模式清空图标 */}
        {multiline && inputValue && (
          <svg
            className={`${styles.icon} ${styles.rightIcon}`}
            aria-hidden="true"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
          >
            <use xlinkHref={rightIcon}></use>
          </svg>
        )}

        {/* ComboBox 模式：下拉箭头图标 */}
        {Type === "ComboBox" && !inputValue && (
          <svg className={`${styles.icon} ${styles.rightIcon}`} aria-hidden="true">
            <use xlinkHref="#icon-arrow-down"></use>
          </svg>
        )}

        {/* Switch 模式：下拉箭头图标 */}
        {Type === "Switch" && !inputValue && (
          <svg className={`${styles.icon} ${styles.rightIcon}`} aria-hidden="true">
            <use xlinkHref="#icon-arrow-down"></use>
          </svg>
        )}

        {/* DatePicker 模式：右侧图标 */}
        {Type === "DatePicker" && (
          inputValue ? (
            <svg
              className={`${styles.icon} ${styles.rightIcon}`}
              aria-hidden="true"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            >
              <use xlinkHref={rightIcon}></use>
            </svg>
          ) : (
            <svg className={`${styles.icon} ${styles.rightIcon}`} aria-hidden="true">
              <use xlinkHref="#icon-rili2"></use>
            </svg>
          )
        )}

        {/* NumberInput 模式：加减按钮 */}
        {Type === "NumberInput" && (
          <div className={styles.numberControls}>
            <button
              className={styles.numberBtn}
              onClick={(e) => { e.stopPropagation(); handleIncrement(); }}
              onMouseDown={(e) => e.preventDefault()}
              tabIndex={-1}
            >
              <svg className={styles.numberIcon} aria-hidden="true">
                <use xlinkHref="#icon-arrow-up-bold"></use>
              </svg>
            </button>
            <button
              className={styles.numberBtn}
              onClick={(e) => { e.stopPropagation(); handleDecrement(); }}
              onMouseDown={(e) => e.preventDefault()}
              tabIndex={-1}
            >
              <svg className={styles.numberIcon} aria-hidden="true">
                <use xlinkHref="#icon-arrow-down-bold"></use>
              </svg>
            </button>
          </div>
        )}

        {/* 下拉面板 */}
        {renderSearchBoxPanel()}
        {renderComboBoxPanel()}
        {renderSwitchPanel()}
        {renderNumberPadPanel()}
        {renderDatePickerPanel()}
      </div>

      {/* Tooltip */}
      {showTooltip && tooltip && (
        <div
          ref={tooltipRef}
          className={getTooltipClassName()}
          style={getTooltipStyle()}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
};

export default TextBox;